import { useEffect, useRef } from "react"
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query"
import { useSubscription } from "@apollo/client"
import { toast } from "sonner"
import type { TFunction } from "i18next"
import type { ResultOf } from "@graphql-typed-document-node/core"
import { graphqlClient } from "@/lib/graphql"
import { graphql } from "@/__generated__"
import { VocabularyEntryStateEnum, type VocabularyEntryOutcomeEnum } from "@/__generated__/graphql"

// The shared read model for vocabulary captures (#825 phase 2). One entry
// selection, repeated verbatim in every document because the public codegen
// project has fragment masking on: a shared fragment would hand consumers a
// masked type they cannot read fields off. The server truth for a capture is
// `state` (storage) + `outcome` (what to show: queued → creating → created /
// failed / cancelled; phase 3 adds corrected) + `failureCode` (why it failed,
// null otherwise); `createdByRole` is who clicked capture, `flashcardIds` the
// materialized cards (empty until completed), `deckId` where they live.
//
// (#825 phase 3) `correctedText` is the spelling the card was built on when
// the AI corrected the capture (null otherwise — `sourceText` is then the
// word), `correctionKind` why (spelling | diacritics | casing | grammar), and
// `skipCorrection` that the student has asked to keep their own spelling: the
// server's signal that a revert is under way or done, which is why no surface
// offers Keep my spelling twice.

export const VOCABULARY_ENTRY_UPDATED = graphql(`
  subscription VocabularyEntryUpdated($lessonId: ID, $conversationId: ID) {
    vocabularyEntryUpdated(lessonId: $lessonId, conversationId: $conversationId) {
      id
      state
      outcome
      failureCode
      lessonId
      sourceMessageId
      createdByRole
      flashcardIds
      deckId
      sourceText
      correctedText
      correctionKind
      skipCorrection
      scheduledAt
      createdAt
      updatedAt
    }
  }
`)

export const MY_VOCABULARY_ENTRIES = graphql(`
  query MyVocabularyEntries($lessonId: ID, $states: [VocabularyEntryStateEnum!], $since: ISO8601DateTime) {
    myVocabularyEntries(lessonId: $lessonId, states: $states, since: $since) {
      id
      state
      outcome
      failureCode
      lessonId
      sourceMessageId
      createdByRole
      flashcardIds
      deckId
      sourceText
      correctedText
      correctionKind
      skipCorrection
      scheduledAt
      createdAt
      updatedAt
      lesson {
        id
        startsAt
        tutorProfile {
          user {
            firstName
          }
        }
      }
    }
  }
`)

export const LESSON_VOCABULARY_ENTRIES = graphql(`
  query LessonVocabularyEntries($lessonId: ID!) {
    lessonVocabularyEntries(lessonId: $lessonId) {
      id
      state
      outcome
      failureCode
      lessonId
      sourceMessageId
      createdByRole
      flashcardIds
      deckId
      sourceText
      correctedText
      correctionKind
      skipCorrection
      scheduledAt
      createdAt
      updatedAt
      lesson {
        id
        startsAt
        tutorProfile {
          user {
            firstName
          }
        }
      }
    }
  }
`)

export const RETRY_VOCABULARY_ENTRY = graphql(`
  mutation RetryVocabularyEntry($input: RetryVocabularyEntryInput!) {
    retryVocabularyEntry(input: $input) {
      vocabularyEntry {
        id
        state
        outcome
        failureCode
        lessonId
        sourceMessageId
        createdByRole
        flashcardIds
        deckId
        sourceText
        correctedText
        correctionKind
        skipCorrection
        scheduledAt
        createdAt
        updatedAt
      }
      errorCode
      errors
    }
  }
`)

// One subscription event (also the shape the capture mutations return).
export type VocabularyEntryUpdate = NonNullable<ResultOf<typeof VOCABULARY_ENTRY_UPDATED>["vocabularyEntryUpdated"]>
// One row of the read queries (the update shape plus the lesson).
export type FeedVocabularyEntry = ResultOf<typeof MY_VOCABULARY_ENTRIES>["myVocabularyEntries"][number]

export const vocabularyEntryKeys = {
  all: ["vocabularyEntries"] as const,
  mine: (args: { lessonId?: string; since?: string }) => ["vocabularyEntries", "mine", args] as const,
  lesson: (lessonId: string) => ["vocabularyEntries", "lesson", lessonId] as const,
}

// The signed-in student's captures (newest first, server-capped at 200).
// staleTime 0: the band re-reads on every mount and after every invalidation.
export function useMyVocabularyEntries(args: { lessonId?: string; since?: string; enabled?: boolean }) {
  const { enabled = true, ...vars } = args
  return useQuery({
    queryKey: vocabularyEntryKeys.mine(vars),
    queryFn: async () => (await graphqlClient.request(MY_VOCABULARY_ENTRIES, vars)).myVocabularyEntries,
    staleTime: 0,
    enabled,
  })
}

// Every non-cancelled capture of one lesson (oldest first). Both participants
// are authorized; anyone else gets [] from the server.
export function useLessonVocabularyEntries(lessonId: string | undefined) {
  return useQuery({
    queryKey: vocabularyEntryKeys.lesson(lessonId ?? ""),
    queryFn: async () =>
      (await graphqlClient.request(LESSON_VOCABULARY_ENTRIES, { lessonId: lessonId as string })).lessonVocabularyEntries,
    enabled: !!lessonId,
  })
}

// Re-enqueues a failed capture. The job reuses the cached Claude text, so a
// retry after a media failure never pays for the text twice.
export function useRetryVocabularyEntry() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) =>
      (await graphqlClient.request(RETRY_VOCABULARY_ENTRY, { input: { id } })).retryVocabularyEntry,
    onSuccess: () => invalidateAfterCapture(qc),
  })
}

// Live server truth for the captures of one lesson OR one conversation (the
// server refuses both/neither, so exactly one variable is ever sent). Rides the
// existing ActionCable split link. `onEntry` is read through a ref so a new
// callback identity never tears the subscription down.
export function useVocabularyEntryUpdates(
  scope: { lessonId: string } | { conversationId: string } | null,
  onEntry: (entry: VocabularyEntryUpdate) => void,
): void {
  const onEntryRef = useRef(onEntry)
  useEffect(() => {
    onEntryRef.current = onEntry
  }, [onEntry])
  useSubscription(VOCABULARY_ENTRY_UPDATED, {
    skip: !scope,
    variables: scope ?? {},
    onData: ({ data }) => {
      const entry = data.data?.vocabularyEntryUpdated
      if (entry) onEntryRef.current(entry)
    },
  })
}

// Stable codes the server leaves on a failed entry (VocabularyEntry::FAILURE_CODES);
// snake_case leaves mirror the server like flashcardsBrowse.regenError.*.
export const FAILURE_CODES = ["ai_error", "ai_unavailable", "media_error", "stuck"] as const

export function failureCodeKey(code: string | null | undefined): string {
  // `code` may arrive as the VocabularyEntryFailureCodeEnum wire value
  // (SCREAMING_SNAKE_CASE — graphql-ruby serializes an enum's name, not its
  // internal `value:`) or already-lowercase, like a FAILURE_CODES constant.
  // Normalize before matching, mirroring entryStateKey's outcome.toLowerCase().
  const normalized = (code ?? "").toLowerCase()
  return (FAILURE_CODES as readonly string[]).includes(normalized)
    ? `flashcards.failureCodes.${normalized}`
    : "flashcards.failureCodes.ai_error"
}

// errorCode values of retryVocabularyEntry whose copy lives under
// flashcards.retryErrors.*; anything else (or a transport failure) shows the
// generic line — except rate_limited, see RETRY_BORROWED_KEYS.
export const RETRY_ERROR_CODES = ["not_found", "not_failed", "not_authenticated"] as const

// Codes the mutation answers with that already have copy elsewhere. Its own
// flood bound (Mutations::RetryVocabularyEntry#redrive_flood?) deliberately
// reuses the capture mutations' `rate_limited` code AND their sentence, so
// point at that leaf rather than duplicating the line under retryErrors in
// all 17 locales.
const RETRY_BORROWED_KEYS: Record<string, string> = {
  rate_limited: "flashcards.captureErrors.rateLimited",
  daily_limit_reached: "flashcards.captureErrors.dailyLimitReached",
}

export function retryErrorKey(code: string | null | undefined): string {
  const borrowed = code ? RETRY_BORROWED_KEYS[code] : undefined
  if (borrowed) return borrowed
  return (RETRY_ERROR_CODES as readonly string[]).includes(code ?? "")
    ? `flashcards.retryErrors.${code}`
    : "flashcards.retryErrors.generic"
}

// Chip label for an entry's outcome (queued / creating / created / failed /
// cancelled). Derived from the enum's wire value — no hand-written map.
export function entryStateKey(outcome: VocabularyEntryOutcomeEnum): string {
  return `flashcards.entryState.${outcome.toLowerCase()}`
}

// After any successful capture, undo or retry: every flashcards query (decks,
// browse, study queues — all rooted at ["flashcards"]) and every entry query.
export function invalidateAfterCapture(qc: QueryClient): void {
  qc.invalidateQueries({ queryKey: ["flashcards"] })
  qc.invalidateQueries({ queryKey: vocabularyEntryKeys.all })
}

// "Keep my spelling" — the single document for the revert, shared by the strip,
// the chat card and Browse (operation names are globally unique, so there can
// only be one). Pending entry → the server just sets skipCorrection and the
// generation job honours it; generated card → a job rebuilds the card on the
// original spelling, keeping its FSRS state.
export const REVERT_VOCABULARY_CORRECTION = graphql(`
  mutation RevertVocabularyCorrection($input: RevertVocabularyCorrectionInput!) {
    revertVocabularyCorrection(input: $input) {
      vocabularyEntry {
        id
        state
        outcome
        failureCode
        lessonId
        sourceMessageId
        createdByRole
        flashcardIds
        deckId
        sourceText
        correctedText
        correctionKind
        skipCorrection
        scheduledAt
        createdAt
        updatedAt
      }
      errorCode
      errors
    }
  }
`)

// Reverts a correction. The card is rebuilt in the background, so the caller
// shows a restoring state and waits for the subscription / a refetch.
export function useRevertVocabularyCorrection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) =>
      (await graphqlClient.request(REVERT_VOCABULARY_CORRECTION, { input: { id } })).revertVocabularyCorrection,
    onSuccess: () => invalidateAfterCapture(qc),
  })
}

// What the student is learning: the corrected spelling when there is one, the
// captured text otherwise. The client mirror of VocabularyEntry#effective_text
// — every surface renders through this, never `sourceText` on its own and
// never its own inline fallback.
export function entryDisplayText(sourceText: string, correctedText?: string | null): string {
  return correctedText?.trim() ? correctedText : sourceText
}

// The original, for the "corrected from ‹…›" line — null when nothing was
// corrected, so a surface can render the line or not from one call.
export function correctedFromText(sourceText: string, correctedText?: string | null): string | null {
  return correctedText?.trim() ? sourceText : null
}

// Keep my spelling is offered on a correction the student has not already
// rejected: `skipCorrection` is the server's "a revert is under way or done",
// and after it finishes `correctedText` is null anyway. The ONE definition —
// a CaptureRow (which carries no skipCorrection) satisfies the parameter type
// and reads as "not yet claimed", which is exactly R4's ruling for the log.
export function canKeepSpelling(entry: { correctedText?: string | null; skipCorrection?: boolean }): boolean {
  return !!entry.correctedText?.trim() && !entry.skipCorrection
}

// The other half: WHO may take it, and when. Mirrors the server's own bound
// (Mutations::RevertVocabularyCorrection — `keep_spelling` for a capture that
// has not been generated yet, `rebuild` once it has). The student owns the card
// and may take either path; a tutor gets the FREE one only, because both
// rebuilds wipe the five text overrides and purge the three media attachments
// the student spent her own daily regeneration slots on — the same bound
// RemoveVocabularyHighlight#can_remove? puts on a tutor, and for the same
// reason. Anyone else — a viewer whose role has not loaded, an admin — is
// offered nothing rather than read as the student.
//
// Shown exactly where the server would say yes: past generation it answers a
// tutor `not_found`, so a surface that still offered it would be inviting a
// refusal. Its own function, not a widening of canKeepSpelling: that one
// answers "is there a correction nobody has claimed", which is a fact about the
// entry, while this is a fact about the viewer.
export function roleCanKeepSpelling(viewerRole: string | undefined, state: VocabularyEntryStateEnum): boolean {
  if (viewerRole === "student") return true
  if (viewerRole === "tutor") {
    return state === VocabularyEntryStateEnum.Pending || state === VocabularyEntryStateEnum.Processing
  }
  return false
}

// Stable codes revertVocabularyCorrection answers a refusal with that have
// their own copy leaf under flashcards.revertErrors.*; anything else — a
// transport failure, or not_authenticated, which the mutation does return but
// deliberately has no leaf — reads as the generic line. `rate_limited` is
// listed rather than borrowing the capture mutations' momentary "give it a
// moment": both bounded revert paths answer it on a DAILY cap, so the
// instruction is "tomorrow".
export const REVERT_ERROR_CODES = ["not_found", "not_correctable", "in_progress", "rate_limited"] as const

export function revertErrorKey(code: string | null | undefined): string {
  return (REVERT_ERROR_CODES as readonly string[]).includes(code ?? "")
    ? `flashcards.revertErrors.${code}`
    : "flashcards.revertErrors.generic"
}

// Ask for a revert and read the answer — the half of "Keep my spelling" that
// is the same everywhere, hoisted out of the three surfaces that had a copy
// each (#825 phase 3). NOT the transport: the chat and the strip call the
// imperative revertFlashcardCorrection, Browse the mutation hook, and only the
// hook invalidates — so the caller supplies `ask` and does its own follow-up.
// What is shared is the interpretation: an errorCode or a missing entry is a
// refusal, its sentence comes from revertErrorKey (which owns every code the
// mutation answers with), and an accepted revert says NOTHING — every surface
// renames its own action to the restoring line, so a toast would be the second
// reading of one sentence.
//
// `transportErrorKey` is the one sentence a caller owns, because a request
// that never arrived is not a refusal the server made: the chat and the strip
// each have their own line for it. Returns the entry the server answered with,
// or null on any refusal — the strip needs that entry to append its own
// capture event, so a bare boolean would have sent it back to a copy.
export async function askKeepSpelling<TEntry>(
  ask: () => Promise<{vocabularyEntry?: TEntry | null; errorCode?: string | null} | null | undefined>,
  {t, transportErrorKey}: {t: TFunction; transportErrorKey: string},
): Promise<TEntry | null> {
  try {
    const result = await ask()
    if (result?.errorCode || !result?.vocabularyEntry) {
      toast.error(t(revertErrorKey(result?.errorCode)))
      return null
    }
    return result.vocabularyEntry
  } catch {
    toast.error(t(transportErrorKey))
    return null
  }
}
